import { useEffect, useState } from "react";
import { _resolveConfirm, type ToastVariant } from "../../utils/globalNotify";

type ToastItem = { id: number; message: string; variant: ToastVariant };
type ConfirmState = { title: string; message: string } | null;

let _nextId = 0;

const variantStyles: Record<ToastVariant, string> = {
  success: "bg-emerald-600 text-white",
  error: "bg-red-600 text-white",
  warning: "bg-amber-500 text-white",
  info: "bg-slate-800 text-white",
};

export function GlobalNotifications() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [confirm, setConfirm] = useState<ConfirmState>(null);

  useEffect(() => {
    const onToast = (e: Event) => {
      const { message, variant } = (e as CustomEvent).detail as { message: string; variant: ToastVariant };
      const id = ++_nextId;
      setToasts((prev) => [...prev, { id, message, variant }]);
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3500);
    };

    const onConfirm = (e: Event) => {
      const { title, message } = (e as CustomEvent).detail as { title: string; message: string };
      setConfirm({ title, message });
    };

    window.addEventListener("g:toast", onToast);
    window.addEventListener("g:confirm", onConfirm);
    return () => {
      window.removeEventListener("g:toast", onToast);
      window.removeEventListener("g:confirm", onConfirm);
    };
  }, []);

  const handleConfirm = (ok: boolean) => {
    setConfirm(null);
    _resolveConfirm(ok);
  };

  return (
    <>
      {/* Toast stack */}
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`rounded-lg px-4 py-2.5 shadow-lg text-sm font-medium animate-in slide-in-from-right-4 fade-in pointer-events-auto ${variantStyles[t.variant]}`}
          >
            {t.message}
          </div>
        ))}
      </div>

      {/* Confirm modal */}
      {confirm && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40">
          <div className="w-[92vw] max-w-md rounded-2xl bg-white shadow-2xl p-6">
            <div className="text-base font-semibold text-slate-900">{confirm.title}</div>
            <div className="mt-2 max-h-[50vh] overflow-auto whitespace-pre-line text-sm text-slate-600">{confirm.message}</div>
            <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
              <button
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition"
                onClick={() => handleConfirm(false)}
              >
                Abbrechen
              </button>
              <button
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition"
                onClick={() => handleConfirm(true)}
              >
                Bestätigen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
